package es.cragma.fichajefacil.ui

import androidx.compose.animation.Crossfade
import androidx.compose.runtime.*
import es.cragma.fichajefacil.DeviceLocation
import es.cragma.fichajefacil.data.AppRepository
import es.cragma.fichajefacil.model.LoginAccessMode
import es.cragma.fichajefacil.model.User
import es.cragma.fichajefacil.model.UserRole
import es.cragma.fichajefacil.ui.i18n.AppLanguage
import es.cragma.fichajefacil.ui.i18n.LocalizedStrings
import es.cragma.fichajefacil.ui.screens.admin.ManagementDashboardScreen
import es.cragma.fichajefacil.ui.screens.auth.CompanyRegisterScreen
import es.cragma.fichajefacil.ui.screens.auth.LoginScreen
import es.cragma.fichajefacil.ui.screens.auth.ModeSelectionDialog
import es.cragma.fichajefacil.ui.screens.auth.TerminatedAccountScreen
import es.cragma.fichajefacil.ui.screens.auth.UnregisteredUserScreen
import es.cragma.fichajefacil.ui.screens.worker.WorkerHomeScreen
import es.cragma.fichajefacil.ui.theme.FichajeFacilTheme

enum class ScreenState {
    LOGIN,
    REGISTER_COMPANY,
    MODE_SELECTION,
    WORKER_HOME,
    MANAGEMENT_DASHBOARD,
    TERMINATED_ACCOUNT,
    UNREGISTERED_USER
}

@Composable
fun AppNavigation(
    initialOAuthEmail: String? = null,
    onGoogleSignInRequested: ((onSuccess: (email: String) -> Unit) -> Unit)? = null,
    onAppleSignInRequested: ((onSuccess: (email: String) -> Unit) -> Unit)? = null,
    onRequestGpsLocation: ((onResult: (DeviceLocation?) -> Unit) -> Unit)? = null
) {
    val repository = remember { AppRepository() }

    var currentLanguage by remember { mutableStateOf(AppLanguage.SPANISH) }
    var currentScreen by remember { mutableStateOf(ScreenState.LOGIN) }
    var currentUser by remember { mutableStateOf<User?>(null) }
    var activeMode by remember { mutableStateOf<LoginAccessMode?>(null) }
    var loginErrorMessage by remember { mutableStateOf<String?>(null) }
    var isLoggingIn by remember { mutableStateOf(false) }

    fun routeUser(user: User) {
        currentUser = user
        loginErrorMessage = null
        val availableAccounts = repository.getUsersByEmail(user.email)
        val activeShift = repository.getActiveClockIn(user.id, user.email)

        if (!user.isActive) {
            currentScreen = ScreenState.TERMINATED_ACCOUNT
        } else if (user.companyId == null && !user.isDemoUser && user.role != UserRole.GLOBAL_ADMIN) {
            currentScreen = ScreenState.UNREGISTERED_USER
        } else if (activeShift != null) {
            // Active shift in progress -> Route straight to Worker Home with live timer!
            activeMode = LoginAccessMode.WORKER
            currentScreen = ScreenState.WORKER_HOME
        } else if (user.role.isManagement || availableAccounts.size > 1) {
            currentScreen = ScreenState.MODE_SELECTION
        } else {
            activeMode = LoginAccessMode.WORKER
            currentScreen = ScreenState.WORKER_HOME
        }
    }

    fun handleLogin(email: String) {
        val cleanEmail = email.trim().lowercase()
        if (cleanEmail.isBlank()) {
            loginErrorMessage = LocalizedStrings.get(currentLanguage).enterEmailError
            return
        }

        isLoggingIn = true
        loginErrorMessage = null

        repository.loginWithCloudSync(cleanEmail) { foundUsers ->
            isLoggingIn = false
            if (foundUsers.isNotEmpty()) {
                val activeShiftUser = foundUsers.find { u -> repository.getActiveClockIn(u.id, u.email) != null }
                if (activeShiftUser != null) {
                    routeUser(activeShiftUser)
                } else if (foundUsers.size > 1) {
                    currentUser = foundUsers.first()
                    currentScreen = ScreenState.MODE_SELECTION
                } else {
                    routeUser(foundUsers.first())
                }
            } else {
                val unregisteredUser = User(
                    id = "unregistered-$cleanEmail",
                    firstName = cleanEmail.substringBefore("@").replace(".", " "),
                    lastName = "",
                    email = cleanEmail,
                    phone = "",
                    companyId = null,
                    role = UserRole.WORKER,
                    position = "Usuario No Registrado",
                    department = "Sin Asignar"
                )
                currentUser = unregisteredUser
                currentScreen = ScreenState.UNREGISTERED_USER
            }
        }
    }

    LaunchedEffect(Unit) {
        if (!initialOAuthEmail.isNullOrBlank()) {
            handleLogin(initialOAuthEmail)
        } else {
            val savedEmail = repository.getSavedSessionEmail()
            if (!savedEmail.isNullOrBlank()) {
                handleLogin(savedEmail)
            }
        }
    }

    FichajeFacilTheme {
        Crossfade(targetState = currentScreen) { screen ->
            when (screen) {
                ScreenState.LOGIN -> {
                    LoginScreen(
                        currentLanguage = currentLanguage,
                        onLanguageChanged = { currentLanguage = it },
                        onLoginSuccess = { email ->
                            handleLogin(email)
                        },
                        onGoogleSignInRequested = onGoogleSignInRequested?.let { req ->
                            {
                                req { authenticatedEmail ->
                                    handleLogin(authenticatedEmail)
                                }
                            }
                        },
                        onAppleSignInRequested = onAppleSignInRequested?.let { req ->
                            {
                                req { authenticatedEmail ->
                                    handleLogin(authenticatedEmail)
                                }
                            }
                        },
                        onNavigateToRegisterCompany = {
                            currentScreen = ScreenState.REGISTER_COMPANY
                        },
                        externalErrorMessage = loginErrorMessage,
                        onClearExternalError = {
                            loginErrorMessage = null
                        },
                        isLoading = isLoggingIn
                    )
                }

                ScreenState.UNREGISTERED_USER -> {
                    val user = currentUser
                    if (user != null) {
                        UnregisteredUserScreen(
                            user = user,
                            currentLanguage = currentLanguage,
                            onNavigateToRegisterCompany = {
                                currentScreen = ScreenState.REGISTER_COMPANY
                            },
                            onBackToLogin = {
                                repository.clearUserSession()
                                currentUser = null
                                activeMode = null
                                currentScreen = ScreenState.LOGIN
                            }
                        )
                    } else {
                        currentScreen = ScreenState.LOGIN
                    }
                }

                ScreenState.TERMINATED_ACCOUNT -> {
                    val user = currentUser
                    val company = user?.companyId?.let { repository.getCompany(it) }
                    if (user != null) {
                        TerminatedAccountScreen(
                            user = user,
                            company = company,
                            currentLanguage = currentLanguage,
                            onBackToLogin = {
                                repository.clearUserSession()
                                currentUser = null
                                activeMode = null
                                currentScreen = ScreenState.LOGIN
                            }
                        )
                    } else {
                        currentScreen = ScreenState.LOGIN
                    }
                }

                ScreenState.REGISTER_COMPANY -> {
                    CompanyRegisterScreen(
                        currentLanguage = currentLanguage,
                        onRegisterSuccess = { companyName, cif, address, city, country, plan, adminName, adminPhone, adminEmail, adminPassword ->
                            val (_, adminUser) = repository.registerCompany(
                                name = companyName,
                                cif = cif,
                                address = address,
                                city = city,
                                country = country,
                                plan = plan,
                                adminName = adminName,
                                adminPhone = adminPhone,
                                adminEmail = adminEmail,
                                adminPassword = adminPassword
                            )
                            repository.saveUserSession(adminUser)
                            currentUser = adminUser
                            currentScreen = ScreenState.MODE_SELECTION
                        },
                        onNavigateBackToLogin = {
                            currentScreen = ScreenState.LOGIN
                        }
                    )
                }

                ScreenState.MODE_SELECTION -> {
                    val user = currentUser
                    if (user != null) {
                        val userAccounts = repository.getUsersByEmail(user.email)
                        ModeSelectionDialog(
                            user = user,
                            availableAccounts = userAccounts,
                            repository = repository,
                            currentLanguage = currentLanguage,
                            onUserSelected = { selectedAccount ->
                                currentUser = selectedAccount
                                repository.saveUserSession(selectedAccount)
                            },
                            onModeSelected = { mode ->
                                activeMode = mode
                                when (mode) {
                                    LoginAccessMode.WORKER -> currentScreen = ScreenState.WORKER_HOME
                                    LoginAccessMode.MANAGEMENT -> currentScreen = ScreenState.MANAGEMENT_DASHBOARD
                                }
                            },
                            onLogout = {
                                repository.clearUserSession()
                                currentUser = null
                                activeMode = null
                                currentScreen = ScreenState.LOGIN
                            }
                        )
                    } else {
                        currentScreen = ScreenState.LOGIN
                    }
                }

                ScreenState.WORKER_HOME -> {
                    val user = currentUser
                    if (user != null) {
                        WorkerHomeScreen(
                            user = user,
                            repository = repository,
                            currentLanguage = currentLanguage,
                            onSwitchToManagementMode = if (user.role.isManagement) {
                                {
                                    activeMode = LoginAccessMode.MANAGEMENT
                                    currentScreen = ScreenState.MANAGEMENT_DASHBOARD
                                }
                            } else null,
                            onLogout = {
                                repository.clearUserSession()
                                currentUser = null
                                activeMode = null
                                currentScreen = ScreenState.LOGIN
                            },
                            onSwitchCompany = {
                                currentScreen = ScreenState.MODE_SELECTION
                            },
                            onRequestGpsLocation = onRequestGpsLocation
                        )
                    } else {
                        currentScreen = ScreenState.LOGIN
                    }
                }

                ScreenState.MANAGEMENT_DASHBOARD -> {
                    val user = currentUser
                    if (user != null) {
                        ManagementDashboardScreen(
                            currentUser = user,
                            repository = repository,
                            currentLanguage = currentLanguage,
                            onSwitchToWorkerMode = {
                                activeMode = LoginAccessMode.WORKER
                                currentScreen = ScreenState.WORKER_HOME
                            },
                            onNavigateToRegisterCompany = {
                                currentScreen = ScreenState.REGISTER_COMPANY
                            },
                            onLogout = {
                                repository.clearUserSession()
                                currentUser = null
                                activeMode = null
                                currentScreen = ScreenState.LOGIN
                            },
                            onSwitchCompany = {
                                currentScreen = ScreenState.MODE_SELECTION
                            }
                        )
                    } else {
                        currentScreen = ScreenState.LOGIN
                    }
                }
            }
        }
    }
}
